import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";

const LANGUAGE_COOKIE_KEY = "chesshood_language";
const SUPPORTED_LANGUAGES = ["en", "pl", "es"];

const LANGUAGE_LABELS = {
  en: "English",
  pl: "Polski",
  es: "Espanol",
};

function normalizeLanguage(value) {
  if (!value || typeof value !== "string") {
    return "en";
  }

  const lower = value.toLowerCase();
  if (SUPPORTED_LANGUAGES.includes(lower)) {
    return lower;
  }

  if (lower.startsWith("pl")) {
    return "pl";
  }

  if (lower.startsWith("es")) {
    return "es";
  }

  return "en";
}

function readInitialLanguage() {
  if (typeof window === "undefined") {
    return "en";
  }

  const storedCookie = Cookies.get(LANGUAGE_COOKIE_KEY);
  if (storedCookie) {
    return normalizeLanguage(storedCookie);
  }

  return normalizeLanguage(window.navigator.language);
}

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  pick: (copy) => (typeof copy === "string" ? copy : ""),
  languageLabels: LANGUAGE_LABELS,
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(readInitialLanguage);

  useEffect(() => {
    Cookies.set(LANGUAGE_COOKIE_KEY, language, { expires: 365, sameSite: "Lax" });

    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage: (nextLanguage) =>
        setLanguageState(normalizeLanguage(nextLanguage)),
      pick: (copy) => {
        if (typeof copy === "string") {
          return copy;
        }

        if (!copy || typeof copy !== "object") {
          return "";
        }

        return copy[language] || copy.en || "";
      },
      languageLabels: LANGUAGE_LABELS,
    }),
    [language]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
